import { ReactNode, useState } from 'react';
import { Card, PieChart, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';
import { formatAmount } from '../formatAmount';
import type { CategoryTotal } from '../insightsModel';
import type { CategoryDescriptor } from '../categories';
import styles from './PieCard.module.scss';

const cn = withStyles(styles);

type PieCardProps = {
  title: string;
  currency: string;
  totals: CategoryTotal[];
  resolveLabel: (descriptor: CategoryDescriptor) => string;
  onSliceClick: (descriptor: CategoryDescriptor) => void;
  onViewTransactions: () => void;
  viewTransactionsLabel: string;
  // Enhanced-tier analytics card slot, rendered between legend and button
  children?: ReactNode;
};

export default function PieCard({
  title,
  currency,
  totals,
  resolveLabel,
  onSliceClick,
  onViewTransactions,
  viewTransactionsLabel,
  children,
}: PieCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment.Insights' });
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);

  const total = totals.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Card className={cn('container')}>
      <div className={cn('header')}>
        <span className={cn('headerLabel')}>{title}</span>

        <span className={cn('headerValue')} data-testid="pie-card-total">
          {formatAmount(total, currency)}
        </span>
      </div>

      {totals.length === 0 ? (
        <p className={cn('emptyState')}>{t('noCategoryData', { defaultValue: 'No transactions in this period.' })}</p>
      ) : (
        <>
          <div className={cn('chartWrap')}>
            <PieChart
              slices={totals.map((item) => ({
                label: resolveLabel(item.descriptor),
                value: item.amount,
                color: item.color,
              }))}
              size={190}
              onSliceClick={(_slice, index) => onSliceClick(totals[index].descriptor)}
              onSliceHover={setHoveredSlice}
            />
          </div>

          <div className={cn('legend', hoveredSlice !== null && 'legendHasHover')}>
            {totals.map((item, index) => (
              <button
                key={item.descriptor.reportType}
                type="button"
                className={cn('legendItem', hoveredSlice === index && 'legendItemHovered')}
                onClick={() => onSliceClick(item.descriptor)}
              >
                <span className={cn('legendName')}>
                  <span className={cn('legendDot')} style={{ background: item.color }} />
                  {resolveLabel(item.descriptor)}
                </span>

                <span className={cn('legendPct')}>{total > 0 ? Math.round((item.amount / total) * 100) : 0}%</span>
                <span className={cn('legendAmount')}>{formatAmount(item.amount, currency)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {children}

      <Button colorScheme="secondary" className={cn('viewButton')} onClick={onViewTransactions}>
        {viewTransactionsLabel}
      </Button>
    </Card>
  );
}
